import { create } from 'zustand'
import { dbgEvent } from '../lib/debug'

/**
 * État RÉACTIF de l'enregistrement vocal, keyé par sessionId (comme status.store.ts et
 * send-queue.store.ts). SANS `persist` : cet état ne doit PAS survivre à un rechargement de
 * page (un MediaRecorder ne se sérialise pas), seulement au remount React déclenché par le
 * changement d'onglet de conversation (`key={activeTab.sessionId}` dans ChatPage.tsx).
 *
 * Les objets NON sérialisables (MediaRecorder, chunks, timer) vivent séparément dans une Map
 * module-scope côté `useVoiceInput.ts` — ce store ne porte que ce qui doit déclencher un
 * re-render (isRecording, isTranscribing, recordingDuration, lastError, canRetry).
 */
export interface VoiceRecordingState {
  isRecording: boolean
  isTranscribing: boolean
  recordingDuration: number
  lastError: string | null
  canRetry: boolean
}

const DEFAULT_RECORDING_STATE: VoiceRecordingState = {
  isRecording: false,
  isTranscribing: false,
  recordingDuration: 0,
  lastError: null,
  canRetry: false,
}

interface VoiceRecordingStoreState {
  recordings: Record<string, VoiceRecordingState>
  setRecording: (sessionId: string, patch: Partial<VoiceRecordingState>) => void
  getRecording: (sessionId: string) => VoiceRecordingState
  resetRecording: (sessionId: string) => void
}

export const useVoiceRecordingStore = create<VoiceRecordingStoreState>((set, get) => ({
  recordings: {},

  setRecording: (sessionId, patch) => {
    dbgEvent('Zustand', 'voiceRecording.setRecording', { sessionId: sessionId.slice(0, 8), patch })
    set(state => ({
      recordings: {
        ...state.recordings,
        [sessionId]: { ...(state.recordings[sessionId] ?? DEFAULT_RECORDING_STATE), ...patch },
      },
    }))
  },

  getRecording: (sessionId) => get().recordings[sessionId] ?? DEFAULT_RECORDING_STATE,

  resetRecording: (sessionId) => {
    set(state => {
      if (!state.recordings[sessionId]) return state
      dbgEvent('Zustand', 'voiceRecording.resetRecording', { sessionId: sessionId.slice(0, 8) })
      const next = { ...state.recordings }
      delete next[sessionId]
      return { recordings: next }
    })
  },
}))
